import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { streamAI } from '../utils/streamAI';

const getSessionId = () => {
  let sid = sessionStorage.getItem('rb_chat_session');
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('rb_chat_session', sid);
  }
  return sid;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "G'day! I'm the Ricky's Bees honey helper. Ask me anything about our honey, shipping or your order. 🍯" }
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setMessages(prev => [...prev, { role: 'user', text }, { role: 'assistant', text: '' }]);
    const append = (chunk) => setMessages(prev => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], text: next[next.length - 1].text + chunk };
      return next;
    });
    await streamAI('/api/chat', { session_id: getSessionId(), message: text },
      append,
      () => setBusy(false),
      (err) => { append(err); setBusy(false); });
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-2xl shadow-2xl border border-border bg-background flex flex-col overflow-hidden" style={{ height: 'min(520px, 70vh)' }} data-testid="chat-panel">
          <div className="bg-primary text-primary-foreground px-5 py-4 flex items-center justify-between">
            <div>
              <p className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Ricky's Bees</p>
              <p className="text-xs opacity-80">Honey helper — ask us anything</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${m.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted text-foreground rounded-bl-md'}`}
                  data-testid={`chat-msg-${m.role}-${i}`}>
                  {m.text || (busy && i === messages.length - 1 ? '…' : '')}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border flex items-center space-x-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="Type your question..."
              className="flex-1 px-4 py-2.5 rounded-full border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="chat-input"
            />
            <button onClick={send} disabled={busy || !input.trim()}
              className="p-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-40"
              data-testid="chat-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-4 md:right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl hover:bg-primary/90 transition-all duration-300 hover:scale-105 flex items-center justify-center"
        data-testid="chat-toggle">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
};

export default ChatWidget;
